import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import { DateRangePicker } from "@syncfusion/ej2-calendars";
import { getComponent } from "@syncfusion/ej2-base";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Filter,
  Group,
  Inject,
  Toolbar,
  ExcelExport,
  PdfExport,
  Resize,
  TextWrapSettingsModel,
} from "@syncfusion/ej2-react-grids";
import { ClickEventArgs } from "@syncfusion/ej2-navigations";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getSupervisor } from "../../redux/features/providers-main/provider-supervisor-slice";
import { getAllBusinessUnits } from "../../redux/features/business-unit/business-unit";
import { ProviderList } from "../../types/provider";
import { OrgBU } from "../../types/company";
import { ClientAuthPayload } from "../../types/client";
import { getTenantAuthorizations } from "../../service/client.service";
import moment from "moment";

const AuthorizationsList: React.FC = () => {
  const [authorizations, setAuthorizations] = useState<
    (ClientAuthPayload & { clientName: string; client?: any })[]
  >([]);

  const PAGE_SIZE = 15;

  const [statusFilter, setStatusFilter] = useState<
    "All" | "active" | "expired" | "upcoming"
  >("active");

  const [buFilter, setBuFilter] = useState<string>("");

  const [authCodeFilter, setAuthCodeFilter] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(true);

  const dispatch = useAppDispatch();
  const supervisors = useAppSelector(
    (state) => state.providerSupervisorSlice.supervisorList
  );
  const businessUnits = useAppSelector(
    (state) => state.businessUnitsSlice.businessUnits
  );

  const gridInstance = React.useRef<GridComponent>(null);

  const textWrapSettings: TextWrapSettingsModel = { wrapMode: "Content" };

  const fetchAuthorizations = async (status: string, buId: string) => {
    try {
      setLoading(true);

      const statusParam = status === "All" ? "" : status;

      const response = await getTenantAuthorizations({
        status: statusParam,
        buId: buId || undefined,
      });

      if (response?.data) {
        setAuthorizations(response.data);
      }
    } catch (error) {
      console.error("Error fetching authorizations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getSupervisor());
    dispatch(getAllBusinessUnits());
    fetchAuthorizations("active", "");
  }, [dispatch]);

  useEffect(() => {
    if (!loading) {
      fetchAuthorizations(statusFilter, buFilter);
    }
  }, [statusFilter, buFilter]);

  const supervisorNameById = useMemo(() => {
    const map: Record<string, string> = {};
    supervisors.forEach((s: ProviderList) => {
      map[s.id] = `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim();
    });
    return map;
  }, [supervisors]);

  const buNameById = useMemo(() => {
    const map: Record<string, string> = {};
    businessUnits.forEach((b: OrgBU) => {
      map[b.id] = b.businessUnitName;
    });
    return map;
  }, [businessUnits]);

  // ---------- derive auth-code dropdown options ----------
  const authCodeOptions = useMemo(() => {
    const codes = new Set<string>();
    authorizations.forEach((a) => {
      (a.auth || []).forEach((x: any) => {
        const code = x?.authCode;
        if (code !== undefined && code !== null && `${code}`.trim() !== "") {
          codes.add(String(code));
        }
      });
    });
    return Array.from(codes).sort();
  }, [authorizations]);

  // ---------- helper to total units ----------
  function computeUnitsTotal(auth: any[]) {
    if (!auth || !auth.length) return 0;
    return auth.reduce(
      (sum: number, it: any) => sum + (Number(it?.units) || 0),
      0
    );
  }

  /* ---------- formatting helpers (defined early so they are in scope everywhere) ---------- */
  function formatAuthPeriod(authPeriod: any) {
    if (!authPeriod) return "-";
    return `${authPeriod.startDate || ""} - ${authPeriod.endDate || ""}`;
  }

  function formatAuthCodes(auth: any[]) {
    if (!auth || !auth.length) return "-";
    return auth.map((item) => item.authCode).join(", ");
  }

  function formatUnits(auth: any[]) {
    if (!auth || !auth.length) return "-";
    return auth.map((item) => item.units).join(", ");
  }

  function formatDate(date: string) {
    if (!date) return "-";
    return moment(date).format("MM/DD/YYYY");
  }

  /**
   * Enriched grid data with concrete fields so Syncfusion filter operates
   * on real properties instead of depending solely on valueAccessor/template.
   */
  const gridData = useMemo(() => {
    return authorizations.map((item) => {
      // business unit names
      const buArr: string[] = item?.client?.businessUnit || [];
      const buNames = buArr.length
        ? buArr.map((id) => buNameById[id] || id).join(", ")
        : "-";

      // supervisor
      const supervisor =
        item.restrictToSupervisor &&
        (supervisorNameById[item.restrictToSupervisor] ||
          item.restrictToSupervisor);

      return {
        ...item,
        buNames,
        authPeriodDisplay: formatAuthPeriod(item.authPeriod),
        authCodesDisplay: formatAuthCodes(item.auth),
        unitsDisplay: formatUnits(item.auth),
        unitsTotal: computeUnitsTotal(item.auth),
        supervisorName: supervisor || "-",
        billingLocationName: item.billingOfficeLocation
          ? buNameById[item.billingOfficeLocation] || item.billingOfficeLocation
          : "-",
        updatedOnDisplay: formatDate(item.updatedAt),
        // backing fields for custom date-range filter
        authPeriodStart: item?.authPeriod?.startDate
          ? new Date(item.authPeriod.startDate)
          : null,
        authPeriodEnd: item?.authPeriod?.endDate
          ? new Date(item.authPeriod.endDate)
          : null,
      };
    });
  }, [authorizations, buNameById, supervisorNameById]);

  const toolbarClick = (args: ClickEventArgs) => {
    if (gridInstance.current) {
      if (args.item.id === "grid_pdfexport") {
        gridInstance.current.showSpinner();
        gridInstance.current.pdfExport();
      } else if (args.item.id === "grid_excelexport") {
        gridInstance.current.showSpinner();
        gridInstance.current.excelExport();
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Title and filters in one row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" className="DetailsPageTitle">
          Client Authorizations
        </Typography>

        {/* Filters aligned to the right */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          {/* Status filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(e) =>
                setStatusFilter(e.target.value as typeof statusFilter)
              }
              MenuProps={{
                disableScrollLock: true,
                keepMounted: true,
                disablePortal: true,
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="upcoming">Upcoming</MenuItem>
            </Select>
          </FormControl>

          {/* Business Unit filter */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="bu-filter-label">Business Unit</InputLabel>
            <Select
              labelId="bu-filter-label"
              value={buFilter}
              label="Business Unit"
              onChange={(e) => setBuFilter(e.target.value as string)}
              MenuProps={{
                disableScrollLock: true,
                keepMounted: true,
                disablePortal: true,
              }}
            >
              <MenuItem value="">All</MenuItem>
              {businessUnits.map((bu) => (
                <MenuItem key={bu.id} value={bu.id}>
                  {bu.businessUnitName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="authcode-filter-label">Auth Code</InputLabel>
            <Select
              labelId="authcode-filter-label"
              value={authCodeFilter}
              label="Auth Code"
              onChange={(e) => {
                const code = (e.target.value as string) || "";
                setAuthCodeFilter(code);
                if (gridInstance.current) {
                  if (!code) {
                    gridInstance.current.removeFilteredColsByField(
                      "authCodesDisplay"
                    );
                  } else {
                    gridInstance.current.filterByColumn(
                      "authCodesDisplay",
                      "contains",
                      code
                    );
                  }
                }
              }}
              MenuProps={{
                disableScrollLock: true,
                keepMounted: true,
                disablePortal: true,
              }}
            >
              <MenuItem value="">All</MenuItem>
              {authCodeOptions.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <Paper elevation={2} sx={{ p: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "65vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <GridComponent
            id="authorizationsGrid"
            dataSource={gridData}
            allowPaging={true}
            pageSettings={{ pageSize: PAGE_SIZE }}
            allowSorting={true}
            allowFiltering={true}
            filterSettings={{ type: "Menu" }}
            allowResizing={true}
            allowTextWrap={true}
            textWrapSettings={textWrapSettings}
            toolbarClick={toolbarClick}
            ref={gridInstance}
            height="65vh"
          >
            <ColumnsDirective>
              <ColumnDirective
                field="clientName"
                headerText="Client"
                width="150"
              />
              <ColumnDirective
                field="buNames"
                headerText="BU"
                width="180"
                valueAccessor={(field: string, data: any) => {
                  const buArr: string[] = data?.client?.businessUnit || [];
                  return buArr.length
                    ? buArr
                        .map((buId: string) => buNameById[buId] || buId)
                        .join(", ")
                    : "-";
                }}
                template={(rowData: any) => {
                  const buArr: string[] = rowData?.client?.businessUnit || [];
                  if (!buArr.length) return "-";
                  return buArr
                    .map((buId: string) => buNameById[buId] || buId)
                    .join(", ");
                }}
              />
              <ColumnDirective
                field="authorizationName"
                headerText="Name"
                width="150"
              />
              <ColumnDirective
                field="insurance.insuranceProviderName"
                headerText="Insurance"
                width="150"
              />
              <ColumnDirective
                field="authorizationNumber"
                headerText="Auth Number"
                width="120"
                type="number"
              />
              <ColumnDirective
                field="authPeriodStart"
                headerText="Auth Period Start"
                type="date"
                format="MM/dd/yyyy"
                template={(rowData: any) => formatDate(rowData.authPeriodStart)}
              />
              <ColumnDirective
                field="authPeriodEnd"
                headerText="Auth Period End"
                type="date"
                format="MM/dd/yyyy"
                template={(rowData: any) => formatDate(rowData.authPeriodEnd)}
              />
              <ColumnDirective
                field="authCodesDisplay"
                headerText="Auth Codes"
                width="150"
                valueAccessor={(field, data: any) => {
                  if (!data) return "-";
                  return formatAuthCodes(data?.auth);
                }}
                template={(rowData: any) => formatAuthCodes(rowData.auth)}
              />
              <ColumnDirective
                field="unitsTotal"
                headerText="# of Units"
                width="120"
                type="number"
                format="N0"
                template={(rowData: any) => rowData.unitsTotal}
              />
              <ColumnDirective
                field="supervisorName"
                headerText="Supervisor"
                width="150"
                valueAccessor={(field, data: any) => {
                  if (!data || !data.restrictToSupervisor) return "-";
                  return (
                    supervisorNameById[data.restrictToSupervisor] ||
                    data.restrictToSupervisor
                  );
                }}
                template={(rowData: any) => {
                  if (!rowData.restrictToSupervisor) return "-";
                  return (
                    supervisorNameById[rowData.restrictToSupervisor] ||
                    rowData.restrictToSupervisor
                  );
                }}
              />
              <ColumnDirective
                field="billingLocationName"
                headerText="Billing Location"
                width="150"
                valueAccessor={(field, data: any) => {
                  if (!data || !data.billingOfficeLocation) return "-";
                  return (
                    buNameById[data.billingOfficeLocation] ||
                    data.billingOfficeLocation
                  );
                }}
                template={(rowData: any) => {
                  if (!rowData.billingOfficeLocation) return "-";
                  return (
                    buNameById[rowData.billingOfficeLocation] ||
                    rowData.billingOfficeLocation
                  );
                }}
              />
              <ColumnDirective
                field="updatedAt"
                headerText="Updated On"
                width="120"
                type="date"
                format="MM/dd/yyyy"
                template={(rowData: any) => formatDate(rowData.updatedAt)}
              />
            </ColumnsDirective>
            <Inject
              services={[
                Page,
                Sort,
                Filter,
                Group,
                Toolbar,
                ExcelExport,
                PdfExport,
                Resize,
              ]}
            />
          </GridComponent>
        )}
      </Paper>
    </Box>
  );
};

export default AuthorizationsList;
